import React, { useRef, useEffect } from 'react';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { Button } from '@src/stories/elements/Button/Button';
import "./ProjectShowcase.css"; // Assuming you have a CSS file for custom styles

export const ProjectShowcase = ({ settings }) => {
  const mainCarouselRef = useRef<Splide>(null);
  const thumbnailCarouselRef = useRef<Splide>(null);

  // Transform Shopify blocks into projects array
  const shopifyProjects = settings?.settings?.blocks ? 
    settings.settings?.blocks.map((project, index) => ({
      id: project.id || (index + 1).toString(),
      title: project.settings.title || project.settings.name || 'Untitled Project',
      subtitle: project.settings.subtitle || '',
      description: project.settings.description || '',
      ctaText: project.settings.cta_text || 'View Case Study',
      ctaUrl: project.settings.cta_url || project.settings.url || '#',
      backgroundImage: project.settings.image || '',
      thumbnail: project.settings.thumbnail || project.settings.image || '',
      logo: project.settings.icon || '',
      category: project.settings.category || '',
      name: project.settings.name || project.settings.title || 'Untitled Project'
    })) : [];

  // Extract settings from Shopify section settings or use defaults
  const {
    projects = shopifyProjects,
    autoplay = settings?.autoplay !== false,
    autoplaySpeed = settings?.autoplay_speed || settings?.autoplaySpeed || 4000,
    showThumbnails = settings?.show_thumbnails !== false && settings?.showThumbnails !== false,
    loop = true,
    drag = settings?.drag !== false,
    pauseOnHover = settings?.pause_on_hover !== false && settings?.pauseOnHover !== false,
    arrows = settings?.arrows === true,
    perPage = settings?.perPage || 1,
    perMove = settings?.perMove || 1,
    gap = settings?.gap || '0',
    padding = settings?.padding || '0',
    thumbnailsPerPage = settings?.thumbnails_per_page || settings?.thumbnailsPerPage || 6,
    thumbnailsGap = settings?.thumbnailsGap || '1rem',
    backgroundColor = settings?.backgroundColor || 'rgba(0, 0, 0, 0.3)',
    textColor = settings?.text_color || settings?.textColor || 'white',
    overlayOpacity = settings?.overlay_opacity || settings?.overlayOpacity || 0.7
  } = settings.settings || {};

  useEffect(() => {
    if (mainCarouselRef.current && thumbnailCarouselRef.current && showThumbnails) {
      const mainSplide = mainCarouselRef.current.splide;
      const thumbnailSplide = thumbnailCarouselRef.current.splide;

      if (mainSplide && thumbnailSplide) {
        mainSplide.sync(thumbnailSplide);
      }
    }
  }, [showThumbnails]);

  const mainCarouselOptions = {
    type: loop ? 'loop' : 'slide',
    rewind: !loop,
    perPage,
    perMove,
    gap,
    padding,
    arrows,
    pagination: false,
    drag,
    autoplay,
    interval: autoplaySpeed,
    pauseOnHover,
    resetProgress: false,
    height: '700px',
    width: '100%',
    cover: true,
    classes: {
      arrows: 'splide__arrows project-showcase-arrows',
      arrow: 'splide__arrow project-showcase-arrow',
      prev: 'splide__arrow--prev project-showcase-arrow--prev',
      next: 'splide__arrow--next project-showcase-arrow--next',
    }
  };

  const thumbnailCarouselOptions = {
    type: loop ? 'loop' : 'slide',
    rewind: !loop,
    perPage: thumbnailsPerPage,
    perMove: 1,
    gap: thumbnailsGap,
    pagination: false,
    arrows: false,
    drag: true,
    focus: 'center' as const,
    isNavigation: true,
    updateOnMove: true,
    height: 'auto',
    cover: true,
    width: '100%',
  };

  return (
    <div className="project-showcase-splide w-full relative">
      {/* Main Carousel */}
      <div className="relative">
        <Splide
          ref={mainCarouselRef}
          options={mainCarouselOptions}
          hasTrack={false}
          className="main-carousel"
        >

         <SplideTrack className={`z-2`}>
            {projects.map((project) => (
              <SplideSlide key={project.id} className="relative">
                <div 
                  className="w-full h-full bg-cover relative"
                  style={{ 
                    backgroundImage: `url(${project.backgroundImage})`,
                    minHeight: 'w-full h-full'
                  }}
                >
                  {/* Overlay */}
                  <div 
                    className="absolute inset-0 bg-black"
                    style={{ opacity: 0.2 }}
                  />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex items-center justify-start z-10">
                    <div className="container mx-auto">
                      <div className="w-full">
                        {/* Logo/Icon */}
                        <div className="absolute w-[64px] h-[64px] top-2 left-2 flex flex-col items-start">
                          {project.logo && (
                            <div 
                              className="w-[64px] h-[64px] rounded-lg backdrop-blur-sm flex items-center justify-center bg-white"
                            >
                              <img 
                                src={project.logo} 
                                alt={`${project.name || project.title} logo`}
                                className="w-12 h-12 object-contain"
                              />
                            </div>
                          )}
                          {project.category && (
                            <span 
                              className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm"
                              style={{ color: textColor }}
                            >
                              {project.category}
                            </span>
                          )}
                        </div>
                        <div 
                            className="absolute bottom-0 left-0 project-overlay p-6 w-full text-[#1C1C1F] h-[250px] flex flex-col justify-end"
                        >
                            <div
                                className="b_large_normal mb-[10.68px]"
                            >
                                {project.name || project.title}
                            </div>
                            <div
                                className="b_regular_light mb-[21.35px]"
                            >
                                {project.description}
                            </div>
                            <a
                                href={project.ctaUrl}
                                className="inline-flex items-center b_a1 w-full"
                            >
                                View Project <span className="ml-4 icon-right-arrow-alt text-[30px]"></span>
                            </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SplideSlide>
            ))}
          </SplideTrack>
        </Splide>
      </div>

   

      {/* Thumbnail Navigation */}
      {showThumbnails && (
        <div className="thumbnails py-[33px]">
          <div className="container relative">
            <Splide
              ref={thumbnailCarouselRef}
              options={thumbnailCarouselOptions}
              hasTrack={false}
              className="thumbnail-carousel"
            >
              <SplideTrack>
                {projects.map((project, index) => (
                  <SplideSlide key={project.id} className="cursor-pointer">
                    <div className="group relative">
                      
                      <div className="text-center">
                        <h4 className="h4">
                          {project.name || project.title}
                        </h4>
                      </div>
                      
                      {/* Active indicator */}
                      {/* <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-black rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" /> */}
                    </div>
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
            <div className="pointer-events-none absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-[#FBF8F4] to-transparent z-20" />
            <div className="pointer-events-none absolute bottom-0 right-0 w-32 h-full bg-gradient-to-l from-[#FBF8F4] to-transparent z-20" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectShowcase;